import React from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { QuickReportData, QuickReportRow } from "../types";
import { QR_COL_WIDTHS, type QRColumnKey } from "../constants";
import { fmtDateDMY } from "../utils";
import type { ReportPrefs } from "../../../../../hooks/useReportPreferences";
import { fmtMoneyWithPrefs } from "../../../../../hooks/useReportPreferences";
import type { Density } from "./CompactViewMenu";

interface Props {
  data: QuickReportData;
  prefs?: ReportPrefs;
  density?: Density;
  groupsExpanded?: boolean;
}

const colW = (w?: number) => (w == null ? {} : { width: w });
const HOVER_EDGE = "md:group-hover:first:shadow-[-32px_0_0_#EEF4FB] md:group-hover:last:shadow-[32px_0_0_#EEF4FB]";

const DENSITY_PAD: Record<Density, string> = {
  compact: "py-1.5",
  normal: "py-2.5",
  cozy: "py-3.5",
  comfort: "py-4.5",
};

function clearedLabel(c: string, empty: "dash" | "blank"): string {
  if (c === "cleared") return "Cleared";
  if (c === "reconciled") return "Reconciled";
  if (c === "not-cleared") return "Not cleared";
  return empty === "dash" ? "-" : "";
}

function Cols({ hidden }: { hidden: QRColumnKey[] }) {
  const h = (k: QRColumnKey) => (hidden.includes(k) ? { display: "none" } : {});
  return (
    <colgroup>
      <col style={{ ...colW(QR_COL_WIDTHS.distributionAccount), ...h("distributionAccount") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.date), ...h("date") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.type), ...h("type") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.refNo), ...h("refNo") }} />
      <col style={h("payee")} />
      <col style={h("description")} />
      <col style={{ ...colW(QR_COL_WIDTHS.accountName), ...h("accountName") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.cleared), ...h("cleared") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.amount), ...h("amount") }} />
      <col style={{ ...colW(QR_COL_WIDTHS.balance), ...h("balance") }} />
    </colgroup>
  );
}

export default function QuickReportTable({ data, prefs, density = "compact", groupsExpanded = true }: Props) {
  const hidden = prefs?.hiddenColumns ?? [];
  const empty = prefs?.emptyCellDisplay ?? "blank";
  const borders = prefs?.borders ?? "default";
  const banded = prefs?.bandedRowColour ?? false;
  const rightAlign = prefs?.columnRightAlign ?? {};
  const effectivePrefs = prefs ?? {
    divideBy1000: false, hideCurrency: false, negativeFormat: "minus" as const, negativeInRed: false, decimals: 2 as const,
  } as any as ReportPrefs;

  const densityPad = DENSITY_PAD[density];

  const cellBorder = (
    borders === "none" ? "" :
    borders === "vertical" || borders === "vertical-outer" ? "border-r border-[#EEF1F4]" :
    borders === "inner" ? "border-r border-b border-[#EEF1F4]" :
    "border-b border-[#EEF1F4]"
  );
  const tableBorder = (borders === "outer" || borders === "horizontal-outer" || borders === "vertical-outer") ? "border border-[#D8DEE6]" : "";

  const hCell = (key: QRColumnKey) => hidden.includes(key) ? { display: "none" } : undefined;
  const fmtMoney = (n: number) => fmtMoneyWithPrefs(n, effectivePrefs);
  const moneyColor = (n: number) => (n < 0 && effectivePrefs.negativeInRed) ? "text-[#8B1538]" : "text-teal-dark";

  const filteredGroups = React.useMemo(() => {
    if (!prefs || !prefs.filterValue || prefs.filterCondition === "is-empty" || prefs.filterCondition === "is-not-empty") {
      return data.groups;
    }
    const q = prefs.filterValue.toLowerCase();
    const match = (v: string): boolean => {
      const lv = String(v).toLowerCase();
      if (prefs.filterCondition === "equals") return lv === q;
      if (prefs.filterCondition === "not-equals") return lv !== q;
      if (prefs.filterCondition === "contains") return lv.includes(q);
      return true;
    };
    const field = prefs.filterBy as keyof QuickReportRow;
    return data.groups
      .map((g) => ({ ...g, rows: g.rows.filter((r) => match(String((r as any)[field] ?? "")))}))
      .filter((g) => g.rows.length > 0);
  }, [data, prefs]);

  const visibleColCount = 10 - hidden.length;
  const colspanBeforeAmount = Math.max(1, 8 - hidden.filter((h) => ["distributionAccount","date","type","refNo","payee","description","accountName","cleared"].includes(h)).length);

  return (
    <>
      <div className="hidden md:block">
        <table className={`w-full border-collapse ${tableBorder}`} style={{ tableLayout: "fixed" }}>
          <Cols hidden={hidden} />
          <tbody>
            {filteredGroups.map((group) => (
              <React.Fragment key={group.key}>
                <tr>
                  <td
                    colSpan={visibleColCount}
                    className="px-3 py-2.5 text-[12px] font-bold text-ink"
                    style={{
                      background: "#F7F9FB",
                      borderBottom: "1px solid #EEF1F4",
                      boxShadow: "-32px 0 0 #F7F9FB, 32px 0 0 #F7F9FB",
                    }}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {groupsExpanded ? <ChevronDown className="w-3 h-3 text-ink2" /> : <ChevronRight className="w-3 h-3 text-ink2" />}
                      {group.label} ({group.rows.length})
                    </span>
                  </td>
                </tr>
                {groupsExpanded && group.rows.map((row, idx) => (
                  <RowView key={row.id} row={row} hidden={hidden} banded={banded} idx={idx} cellBorder={cellBorder} empty={empty} rightAlign={rightAlign} fmtMoney={fmtMoney} moneyColor={moneyColor} densityPad={densityPad} />
                ))}
                <tr>
                  <td colSpan={colspanBeforeAmount} className="py-2.5 px-3 text-[12.5px] font-bold text-ink bg-white" style={{ borderTop: "1px solid #D8DEE6", borderBottom: "2px solid #D8DEE6" }}>Total for {group.label}</td>
                  <td className="py-2.5 px-3 text-[12.5px] font-bold text-right tabular-nums bg-white" style={{ ...hCell("amount"), borderTop: "1px solid #D8DEE6", borderBottom: "2px solid #D8DEE6" }}>
                    <span className={moneyColor(group.subtotal)}>{fmtMoney(group.subtotal)}</span>
                  </td>
                  <td className="py-2.5 px-3 text-[12.5px] font-bold text-right tabular-nums bg-white" style={{ ...hCell("balance"), borderTop: "1px solid #D8DEE6", borderBottom: "2px solid #D8DEE6" }}>
                    <span className={moneyColor(group.endBalance)}>{fmtMoney(group.endBalance)}</span>
                  </td>
                </tr>
              </React.Fragment>
            ))}
            <tr>
              <td colSpan={colspanBeforeAmount} className="py-3 px-3 text-[13.5px] font-extrabold text-ink uppercase tracking-wider" style={{ background: "#FAFBFC" }}>Total</td>
              <td className="py-3 px-3 text-[13.5px] font-extrabold text-right tabular-nums" style={{ ...hCell("amount"), background: "#FAFBFC" }}>
                <span className={moneyColor(data.total)}>{fmtMoney(data.total)}</span>
              </td>
              <td className="py-3 px-3 text-[13.5px] font-extrabold text-right tabular-nums" style={{ ...hCell("balance"), background: "#FAFBFC" }}>
                <span className={moneyColor(data.endBalance)}>{fmtMoney(data.endBalance)}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="md:hidden -mx-4">
        {filteredGroups.map((group) => (
          <div key={group.key}>
            <div className="px-4 py-2 bg-[#F7F9FB] border-t border-b border-[#D8DEE6] text-[11px] font-bold text-ink">{group.label} ({group.rows.length})</div>
            {groupsExpanded && group.rows.map((row) => (
              <div key={row.id} className="px-4 py-3 border-b border-[#EEF1F4] flex justify-between gap-3">
                <div>
                  <div className="text-xs font-semibold text-ink">{row.payee}</div>
                  <div className="text-[11px] text-ink3 mt-0.5">{fmtDateDMY(row.date)} &middot; {row.type} {row.refNo}</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className={`text-[12.5px] font-bold tabular-nums ${moneyColor(row.amount)}`}>{fmtMoney(row.amount)}</div>
                  <div className="text-[10.5px] text-ink3 tabular-nums mt-0.5">bal {fmtMoney(row.balance)}</div>
                </div>
              </div>
            ))}
            <div className="px-4 py-2.5 border-t border-b-2 border-[#D8DEE6] bg-white flex justify-between text-xs font-bold">
              <span>Total for {group.label}</span>
              <span className={`tabular-nums ${moneyColor(group.subtotal)}`}>{fmtMoney(group.subtotal)}</span>
            </div>
          </div>
        ))}
        <div className="px-4 py-3 bg-[#FAFBFC] flex justify-between text-[13px] font-extrabold uppercase tracking-wider">
          <span>Total</span>
          <span className={`tabular-nums ${moneyColor(data.total)}`}>{fmtMoney(data.total)}</span>
        </div>
      </div>
    </>
  );
}

function RowView({ row, hidden, banded, idx, cellBorder, empty, rightAlign, fmtMoney, moneyColor, densityPad }: {
  row: QuickReportRow; hidden: QRColumnKey[]; banded: boolean; idx: number; cellBorder: string; empty: "dash" | "blank"; rightAlign: Partial<Record<QRColumnKey, boolean>>; fmtMoney: (n: number) => string; moneyColor: (n: number) => string; densityPad: string;
}) {
  const bandBg = banded && idx % 2 === 1 ? "bg-[#FAFBFC]" : "";
  const cell = `${densityPad} px-3 text-[12.5px] group-hover:bg-[#EEF4FB] ${HOVER_EDGE} ${cellBorder} ${bandBg}`;
  const h = (k: QRColumnKey) => hidden.includes(k) ? { display: "none" } : undefined;
  const ra = (k: QRColumnKey) => rightAlign[k] ? "text-right" : "";
  const dash = empty === "dash" ? "-" : "";

  return (
    <tr className="group">
      <td className={`${cell} text-ink2 ${ra("distributionAccount")}`} style={h("distributionAccount")}>{row.distributionAccount || dash}</td>
      <td className={`${cell} text-ink ${ra("date")}`} style={h("date")}>{fmtDateDMY(row.date) || dash}</td>
      <td className={`${cell} text-ink ${ra("type")}`} style={h("type")}>{row.type || dash}</td>
      <td className={`${cell} text-ink3 ${ra("refNo")}`} style={h("refNo")}>{row.refNo || dash}</td>
      <td className={`${cell} text-ink ${ra("payee")}`} style={h("payee")}>{row.payee || dash}</td>
      <td className={`${cell} text-ink ${ra("description")}`} style={h("description")}>{row.description || dash}</td>
      <td className={`${cell} text-ink ${ra("accountName")}`} style={h("accountName")}>{row.accountName || dash}</td>
      <td className={`${cell} text-ink ${ra("cleared")}`} style={h("cleared")}>{clearedLabel(row.cleared, empty)}</td>
      <td className={`${cell} font-semibold text-right tabular-nums ${moneyColor(row.amount)}`} style={h("amount")}>{fmtMoney(row.amount)}</td>
      <td className={`${cell} font-semibold text-right tabular-nums ${moneyColor(row.balance)}`} style={h("balance")}>{fmtMoney(row.balance)}</td>
    </tr>
  );
}